import { isPresent } from '@luminovo/commons';
import { formatDecimal } from '@luminovo/frontend-utils';
import { styled } from '@mui/material';
import { CircleCheckIcon } from 'lucide-react';
import * as React from 'react';

import { colorSystem, disabledStyles, focusedStyles, h5, hoveredStyles } from '../../theme';
import { Flexbox } from '../Flexbox';
import type { IconElement } from '../icons/IconElement';
import { iconSlot } from '../icons/iconSlot';
import { Text } from '../Text';

export type CheckableChipProps = {
    label: string;
    selected: boolean;
    disabled?: boolean;
    done?: boolean;
    count?: number;
    showEllipsis?: boolean;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    /** Shown before the label once the chip is `done`. Drawn at 14px in the chip's icon color. */
    startIcon?: IconElement;
    /** Shown after the label. Drawn at 14px in the chip's icon color. */
    endIcon?: IconElement;
};

export const CheckableChip = React.forwardRef(
    (
        {
            label,
            selected,
            disabled,
            done,
            count,
            showEllipsis,
            onClick,
            startIcon = <CircleCheckIcon />,
            endIcon,
        }: CheckableChipProps,
        ref: React.Ref<HTMLDivElement>,
    ): JSX.Element => {
        const style = React.useMemo(() => {
            if (disabled) {
                return { ...disabledStyles, color: colorSystem.neutral[6] };
            }
            if (selected) {
                return { ...focusedStyles, color: colorSystem.neutral[8] };
            }
        }, [disabled, selected]);

        const iconColor = React.useMemo(() => {
            if (disabled) {
                return colorSystem.neutral[7];
            }
            if (done) {
                return colorSystem.green[7];
            }
            if (selected) {
                return colorSystem.primary[7];
            }
        }, [disabled, selected, done]);

        return (
            <Container
                data-testid="checkable-chip"
                role="button"
                data-done={done}
                data-disabled={disabled}
                data-selected={selected}
                ref={ref}
                onClick={!disabled ? onClick : undefined}
                style={{ ...style }}
                sx={{ '& > svg': { color: iconColor } }}
            >
                {done && startIcon}
                <Text variant="h5" color="inherit" showEllipsis={showEllipsis} style={{ whiteSpace: 'nowrap' }}>
                    {label}
                </Text>
                {endIcon}
                {isPresent(count) && <Counter>{formatDecimal(count)}</Counter>}
            </Container>
        );
    },
);

const Counter = styled('div')({
    background: colorSystem.neutral[2],
    borderRadius: '8px',
    padding: '0px 4px',
    height: '16px',
    alignItems: 'center',
    ...h5,
});

const Container = styled(Flexbox)({
    // Sizes the start and end icons: call sites pass no size.
    ...iconSlot(14),
    alignItems: 'center',
    gap: '4px',
    width: 'fit-content',
    boxSizing: 'border-box',
    border: `1px solid ${colorSystem.neutral[2]}`,
    cursor: 'pointer',
    padding: '6px 10px',
    borderRadius: '20px',
    background: colorSystem.neutral.white,
    color: colorSystem.neutral[7],
    '&:hover': {
        ...hoveredStyles,
    },
    '&:active': {
        ...focusedStyles,
    },
    '&:focus': {
        ...focusedStyles,
    },
    transition: 'background 0.1s ease-in-out, box-shadow 0.1s ease-in-out',
});
